"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Lock,
  Sparkles,
  ShieldCheck,
  Crown,
  Brain,
  Mic,
  ArrowRight,
  Eye,
  Heart,
  Compass,
  Award,
  Activity,
  Briefcase,
  Building2,
  Target,
  Users,
  GitBranch,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { TabsBlock } from "@/lib/blocks/tabs-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const DOMAINS = [
  {
    icon: Eye,
    title: "Operational Assessment and Advisory",
    body: "Forensic diagnosis of organizational challenges at the highest levels — mapping not just symptoms but root causes, not just challenges but structural vulnerabilities, not just gaps but the systemic patterns that created them. Intelligence-grounded diagnoses that conventional advisory cannot match. The output is an operating mechanism — protocols, checklists, and decision frameworks deployed immediately.",
    accent: "red",
  },
  {
    icon: Building2,
    title: "Governance Transformation Counsel",
    body: "Implementation of governance systems at national scale — the transformation that redesigns how institutions execute, how decisions are made, and how accountability is maintained across complex organizational landscapes. Operational change — verified improvements in governance effectiveness, execution speed, and organizational resilience.",
    accent: "purple",
  },
  {
    icon: ShieldCheck,
    title: "Crisis Response Coordination",
    body: "The clinical method for crisis management under sovereign conditions — triage under pressure, protocol-driven response, and operational continuity when systems fail. The coordination draws from the crisis management protocols that have sustained sovereign-scale operations across eighteen countries. The output is organizational survival and recovery — measured by outcomes.",
    accent: "cyan",
  },
  {
    icon: Target,
    title: "Strategic Intelligence Review",
    body: "Evidence-based decision-making at sovereign scale — leveraging proprietary intelligence infrastructure to provide intelligence-grounded strategic assessment. The review draws from data across hundreds of social platforms, news sources, and languages. Actionable intelligence that transforms strategic decision-making from intuition-based to evidence-based.",
    accent: "emerald",
  },
  {
    icon: Users,
    title: "Leadership Development at the Highest Levels",
    body: "One-on-one mentorship for leaders operating at sovereign scale — transferring the operating method, the clinical discipline, and the decade of operational experience to the individuals who will deploy it at the highest levels of organizational and national leadership. The output is embedded capability — the operating method internalized, not merely understood.",
    accent: "amber-gold",
  },
];

const PILLARS = [
  {
    title: "Operational Security as Religion",
    body: "Every private engagement is conducted under the same security protocols that govern the most sensitive sovereign deployments: compartmentalized operations, need-to-know access controls, encrypted communications, continuous monitoring. The principle is not aspirational. It is operational.",
    accent: "red",
  },
  {
    title: "Compartmentalized Operations",
    body: "A sensitivity-level architecture ensures every participant sees only what they need to see — and nothing more. The compartmentalization is not administrative. It is technical — enforced by platform architecture, not by trust or policy. The unified command sees everything. Individual participants see only their designated scope.",
    accent: "purple",
  },
  {
    title: "Binding Agreements",
    body: "Every private engagement operates under binding non-disclosure agreements tailored to the specific sensitivity level. The agreements cover all parties — Dr. Kush, the operational team, and any third parties brought into scope. The agreement architecture is itself compartmentalized: each party&apos;s obligations defined precisely.",
    accent: "cyan",
  },
  {
    title: "Zero Information Leakage",
    body: "The standard is absolute. Zero leaks. Zero breaches. Zero incidents. Across fifteen-plus years of sovereign-level engagements, the security record stands at zero incidents. This is not luck. This is architecture — security built into every layer, with no third-party dependencies and no off-the-shelf components.",
    accent: "emerald",
  },
  {
    title: "Operational Freedom",
    body: "The discretion standard produces a unique capability: the ability to address challenges that cannot be addressed through any channel involving public exposure. The diagnostic conversation can be candid, the prescription can be honest, and the implementation can be decisive — because the engagement is not performing for an audience.",
    accent: "amber-gold",
  },
];

const CLIENTS = [
  { icon: Crown, title: "Heads of State", body: "Sovereign-level entities and heads of state whose operational challenges carry national consequences and whose discretion requirements prohibit any public profile.", accent: "red" },
  { icon: Heart, title: "Royal Households & Dynasties", body: "Reputation protection, privacy enforcement, and legacy narrative management with absolute discretion. National security level confidentiality across multi-generational structures.", accent: "purple" },
  { icon: ShieldCheck, title: "Defense Leadership", body: "Defense agencies and institutions requiring sovereign-grade operational consulting, crisis management, and strategic intelligence review under the most stringent confidentiality standards.", accent: "cyan" },
  { icon: Award, title: "Fortune-Scale Executives", body: "Senior executives whose operational exposure carries national consequences and whose challenges demand the full weight of sovereign-scale operational expertise.", accent: "emerald" },
  { icon: Building2, title: "Sovereign Institutions", body: "Government bodies and national institutions facing operational transformation, governance modernization, or strategic challenges at national scale.", accent: "amber-gold" },
  { icon: Briefcase, title: "International Organizations", body: "Multilateral institutions and international bodies requiring cross-jurisdictional operational transformation grounded in sovereign-scale experience.", accent: "denim" },
];

const PROCESS = [
  { num: "01", title: "Introduction", body: "Access is by introduction only — the prerequisite for sovereign-level work where confidentiality is non-negotiable and the diagnostic conversation requires candor that only established trust can produce. The introduction establishes the initial trust framework." },
  { num: "02", title: "Diagnostic Conversation", body: "The diagnostic conversation maps the situation with clinical precision: organizational context, stakes, existing capabilities, gaps, root causes, systemic patterns. Not every diagnostic conversation leads to an engagement. The commitment is to produce results, not to accept engagements." },
  { num: "03", title: "Engagement Design", body: "The engagement design specifies the scope, format, duration, milestones, and outcome metrics. Grounded in the diagnostic data, not in a predetermined service preference. The design is calibrated to the specific sensitivity level and operational requirements." },
  { num: "04", title: "Execution Under Discretion", body: "The engagement proceeds under the same compartmentalized architecture that protects sovereign-level operations. Every participant sees only what they need to see. The unified command sees everything. The infrastructure that took a decade to build supports every engagement." },
  { num: "05", title: "Verified Outcomes", body: "Outcomes are measured against the metrics established during the engagement design. The same accountability that governs medical practice governs every private engagement. The benchmark is operational performance — verified, measurable, sovereign-grade.", accent: "" },
];

const GAINS = [
  { icon: Eye, title: "Sovereign-Grade Diagnosis", body: "Intelligence-grounded diagnoses that conventional advisory cannot match. The diagnostic draws from the full proprietary intelligence infrastructure, processing data at sovereign scale to produce the diagnostic picture that informs every recommendation.", accent: "red" },
  { icon: Lock, title: "Absolute Discretion", body: "National security level confidentiality, binding agreements, compartmentalized operations. The structural foundation of how every engagement operates — designed into the architecture, enforced by infrastructure, validated by a track record of zero security incidents.", accent: "purple" },
  { icon: ShieldCheck, title: "Crisis Recovery Capability", body: "The clinical method for crisis management under sovereign conditions — triage under pressure, protocol-driven response, operational continuity when systems fail. Organizational survival and recovery measured by outcomes.", accent: "cyan" },
  { icon: Target, title: "Strategic Intelligence", body: "Evidence-based decision-making at sovereign scale — intelligence-grounded strategic assessment that transforms decision-making from intuition-based to evidence-based. Actionable intelligence across hundreds of platforms and languages.", accent: "emerald" },
  { icon: Users, title: "Embedded Leadership Capability", body: "The operating method transferred to leaders at the highest levels. Embedded capability, not merely understood. The clinical discipline, the operational experience, and the decade of verified outcomes internalized as the leader&apos;s own practice.", accent: "amber-gold" },
  { icon: Brain, title: "Triple Threat Perspective", body: "Cross-domain analysis that no single-domain expert can match — medicine, technology, and politics as one operating method. The intersection where the most demanding sovereign-level problems are solved.", accent: "denim" },
];

export default function PrivateEngagementsPage() {
  return (
    <>
      <PageHero
        icon={Lock}
        overline="Service · Private Engagements"
        overlineClassName="text-denim"
        title={
          <>
            Discreet, High-Stakes, <span className="text-gradient-premium">By Introduction Only.</span>
          </>
        }
        subtitle="Sovereign-grade operational interventions for the most demanding clients in the world. National security level confidentiality, binding agreements, compartmentalized operations, and verified outcomes that never become public record. Access is by introduction only."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Private Engagements" }]}
        badge="Absolute discretion as structural foundation"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
              <Sparkles className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Discretion is not a policy. It is the architecture.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The discretion standard is not a policy preference. It is the structural foundation of how every engagement operates — designed into the architecture, enforced by the infrastructure, and validated by a track record of zero security incidents across fifteen-plus years of sovereign-level work. The standard exceeds what most organizations require. It is calibrated for the clients who require it.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The engagements are not available to every organization. They are curated — offered only to clients whose challenges justify the full weight of sovereign-scale operational expertise, whose discretion requirements match the security infrastructure maintained, and whose outcomes will be measured by the same standard that governs medical practice. The curation is not elitism for its own sake. It is operational discipline.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Private engagements are reserved for clients whose challenges are worthy of the sovereign-scale infrastructure that supports them. The diagnostic conversation determines whether the engagement is appropriate, what the engagement should address, and how it will be measured. No engagement is prescribed without diagnosis. No scope is defined without understanding the landscape.
            </p>
          </div>

          <div className="space-y-3">
            {PILLARS.map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <Lock className="size-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Eye className="size-3.5 text-purple" />
            <span className="text-overline text-purple">What Private Engagements Cover</span>
          </div>
          <h2 className="text-display-md mb-4">Five domains of sovereign-scale intervention.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain addresses a specific category of sovereign-level challenge, each drawing from the integrated operational infrastructure, and each governed by the same discretion standard that defines the entire engagement category.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <d.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <GitBranch className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Process</span>
          </div>
          <h2 className="text-display-md mb-4">From introduction to verified outcomes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every private engagement follows a structured sequence. The introduction establishes trust. The diagnostic conversation maps the landscape. The engagement design specifies the scope. Execution proceeds under the compartmentalized architecture. Outcomes are verified.
          </p>
        </div>
        <div className="space-y-3">
          {PROCESS.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring} group-hover:scale-110 transition-all`}>
                    <span className="text-body-md font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Crown className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Who Engages Privately</span>
          </div>
          <h2 className="text-display-md mb-4">The client landscape for private engagements.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The private engagements serve the most demanding clients in the world — those whose operational challenges carry national consequences and whose discretion requirements prohibit any public profile.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CLIENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <c.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{c.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Private Engagements Deliver</span>
          </div>
          <h2 className="text-display-md mb-4">Six structural capabilities.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each capability draws from the integrated operational infrastructure and the sovereign-grade security architecture — the infrastructure that took a decade to build and that cannot be replicated without the same investment.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Lock className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Engagement Scope</span>
          </div>
          <h2 className="text-display-md mb-4">The full scope of private engagement work.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Private engagements span the full spectrum of sovereign-level intervention. Each scope is calibrated to the specific challenge and executed under the same compartmentalized architecture.
          </p>
        </div>
        <AccordionBlock
          searchable
          defaultOpen={0}
          items={[
            { q: "Sovereign Diagnosis and Strategic Assessment", a: "Intelligence-grounded diagnostic at the highest levels — mapping not just symptoms but root causes, not just challenges but structural vulnerabilities. The diagnostic draws from proprietary intelligence infrastructure processing data at sovereign scale. The output is operational: an operating mechanism deployable immediately.", category: "Strategic" },
            { q: "Governance Transformation at National Scale", a: "Implementation of governance systems that redesign how institutions execute, how decisions are made, and how accountability is maintained across complex organizational landscapes. The transformation produces measured improvement in governance effectiveness, execution speed, and organizational resilience.", category: "Governance" },
            { q: "Crisis Response and Recovery Coordination", a: "The clinical method for crisis management under sovereign conditions — triage under pressure, protocol-driven response, operational continuity when systems fail. The coordination draws from crisis management protocols sustained across sovereign-scale operations in eighteen countries. The output is organizational survival and recovery — measured by outcomes.", category: "Crisis" },
            { q: "Reputation and Narrative Sovereignty", a: "Strategic narrative architecture for sovereign-level entities. The work covers reputation protection, narrative defense, perception management at national scale, and the operational discipline that prevents reputational compromise. The output is a narrative infrastructure that serves the entity's strategic interests.", category: "Strategic" },
            { q: "Strategic Intelligence and Decision Support", a: "Evidence-based decision-making infrastructure at sovereign scale. The service draws from data across hundreds of social platforms, news sources, and languages — proprietary intelligence that transforms strategic decision-making from intuition-based to evidence-based. Actionable intelligence delivered with the discretion the stakes demand.", category: "Intelligence" },
            { q: "Crisis Communications and Stakeholder Coordination", a: "The discipline of communicating under sovereign-level pressure — stakeholder mapping, message architecture, timing protocols, and the operational coordination that prevents communication failures during crisis. The work is conducted under the same compartmentalized architecture that protects all engagement work.", category: "Crisis" },
            { q: "Family Office and Dynasty Governance", a: "Governance mechanisms that protect family wealth, ensure intergenerational transfer, and maintain operational discipline across complex, multi-jurisdictional asset structures. National security level confidentiality and compartmentalized operations are foundational. The output is governance infrastructure calibrated to the dynasty's specific structure.", category: "Governance" },
            { q: "Transition and Succession Planning", a: "Operational planning for leadership transitions, ownership transfers, and generational handovers at sovereign scale. The work covers protocol design, capability transfer, institutional continuity, and the operational discipline that ensures transitions strengthen rather than weaken the organization.", category: "Governance" },
            { q: "Specialized Investigations and Intelligence Reviews", a: "Forensic reviews of operational incidents, security events, governance failures, and strategic intelligence questions. The reviews are conducted under the same security standards that govern sovereign-level operations. The output is verified findings with operational recommendations — not abstract analysis.", category: "Intelligence" },
            { q: "Embedded Leadership Counsel", a: "Long-term counsel for leaders operating at sovereign scale — the operating method transferred to the individuals who will deploy it at the highest levels. The work continues as long as the leader benefits, with the same discretion and outcome accountability that governs all private engagements.", category: "Counsel" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <ShieldCheck className="size-3.5 text-red" />
            <span className="text-overline text-red">Sensitivity Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Three sensitivity tiers, three operating standards.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each private engagement is classified by sensitivity tier. The tier determines the operating standard — security architecture, communication protocols, compartmentalization, and reporting cadence.
          </p>
        </div>
        <CardGridBlock
          variant="icon"
          columns={3}
          items={[
            { title: "Tier One — Sovereign", body: "The highest sensitivity tier — heads of state, royal households, defense leadership, sovereign institutions. The operating standard includes full compartmentalization, sovereign-grade communication infrastructure, zero third-party dependencies, and continuous monitoring. Engagement outcomes never become public record.", accent: "red" },
            { title: "Tier Two — High-Stakes", body: "Fortune-scale executives, international organizations, family offices, political organizations. The operating standard includes compartmentalized operations, encrypted communications, binding agreements, and audit trails. Engagement outcomes remain confidential under all circumstances.", accent: "purple" },
            { title: "Tier Three — Confidential", body: "Senior executives, governance transformations, crisis response, strategic reviews. The operating standard includes binding NDAs, secure communications, and operational discretion. Engagement outcomes are confidential but engagement identity may be acknowledged at the client's discretion.", accent: "cyan" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Eye className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Engagement Considerations</span>
          </div>
          <h2 className="text-display-md mb-4">What the diagnostic conversation covers.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The initial diagnostic conversation maps five dimensions of the engagement. The conversation is itself confidential — conducted under the same standards that govern the engagement itself.
          </p>
        </div>
        <TabsBlock
          defaultValue="context"
          tabs={[
            {
              label: "Context",
              value: "context",
              icon: <Compass className="size-3.5" />,
              content: (
                <div className="space-y-3 text-sm leading-relaxed text-platinum-300/90">
                  <p>The organizational context — entity type, ownership structure, governance architecture, and the regulatory environment. The diagnostic maps the political, economic, and operational landscape within which the engagement operates.</p>
                  <p>Stakes, exposure, and the consequences of failure or success. The sensitivity tier is determined by the context: sovereign entities face different exposures than private organizations, and the operating standard adapts accordingly.</p>
                  <p>Existing capabilities, infrastructure, and dependencies. The diagnostic identifies what the organization can do internally and what requires the consulting infrastructure — informing the engagement design.</p>
                </div>
              ),
            },
            {
              label: "Challenge",
              value: "challenge",
              icon: <Target className="size-3.5" />,
              content: (
                <div className="space-y-3 text-sm leading-relaxed text-platinum-300/90">
                  <p>The specific operational challenge the engagement addresses — governance transformation, crisis response, strategic intelligence, leadership development, or one of the other engagement scopes.</p>
                  <p>Root causes, systemic patterns, and structural vulnerabilities. The diagnostic identifies not just the presenting challenge but the underlying conditions that produced it — addressing symptoms alone produces no lasting change.</p>
                  <p>Timeline, urgency, and the operational constraints. The challenge is mapped against the organization's capacity to absorb change, the political calendar, and the operational windows during which transformation can occur.</p>
                </div>
              ),
            },
            {
              label: "Outcomes",
              value: "outcomes",
              icon: <Award className="size-3.5" />,
              content: (
                <div className="space-y-3 text-sm leading-relaxed text-platinum-300/90">
                  <p>The verified outcomes the engagement is designed to produce — operational improvements, governance effectiveness, crisis recovery, leadership capability, or strategic intelligence.</p>
                  <p>Measurement framework and benchmarks. The engagement design specifies the metrics, the data sources, the reporting cadence, and the verification protocols. Outcomes are measured, not assumed.</p>
                  <p>Long-term operational changes the engagement is designed to enable. The consulting produces transformation that persists beyond the engagement — institutional capability, not dependency on the advisor.</p>
                </div>
              ),
            },
            {
              label: "Security",
              value: "security",
              icon: <Lock className="size-3.5" />,
              content: (
                <div className="space-y-3 text-sm leading-relaxed text-platinum-300/90">
                  <p>Confidentiality requirements and the sensitivity tier. The diagnostic determines the operating standard — sovereign, high-stakes, or confidential — and the corresponding security architecture.</p>
                  <p>Communication infrastructure, compartmentalization requirements, and access controls. The security architecture is designed to meet the engagement's specific requirements, not a generic template.</p>
                  <p>Binding agreements, jurisdictional considerations, and the legal architecture. Every engagement operates under tailored NDAs and the legal frameworks that protect all parties.</p>
                </div>
              ),
            },
            {
              label: "Alignment",
              value: "alignment",
              icon: <Heart className="size-3.5" />,
              content: (
                <div className="space-y-3 text-sm leading-relaxed text-platinum-300/90">
                  <p>Mutual alignment on engagement scope, outcomes, and the operating standard. The diagnostic determines whether the engagement is appropriate — not every inquiry leads to an engagement.</p>
                  <p>Compatibility between the consulting methodology and the organization's culture, governance, and operational discipline. The engagement works when both parties operate from a shared understanding of how excellent things run.</p>
                  <p>Trust framework, communication cadence, and the relationship architecture. The diagnostic conversation establishes the foundation for the engagement's success.</p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-denim/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Lock className="size-10 text-denim mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Request an introduction.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Private engagements are available by introduction only. The diagnostic conversation determines whether the engagement is appropriate, what the engagement should address, and how it will be measured. The commitment is to produce results, not to accept engagements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Request Introduction
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/board-service/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                Board Service
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Board Service", description: "Sovereign-grade governance oversight at the board level.", href: "/services/board-service/", icon: Building2, accent: "cyan" },
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Operational Consulting", description: "Sovereign-scale operational transformation across organizations.", href: "/services/operational-consulting/", icon: Activity, accent: "amber-gold" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "denim" },
        ]}
      />
    </>
  );
}
